import React from 'react';

const fmt = (n) => Math.round(n).toLocaleString('en-IN');
const BAR_MODES = [['before', 'Before'], ['after', 'After'], ['both', 'Compare']];

const ScoreBar = ({ value, color }) => (
  <span className="csr-bar">
    <span className="csr-bar__track"><span className="csr-bar__fill" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} /></span>
    <span className="csr-bar__val">{value}</span>
  </span>
);

/**
 * Soil health score by state / by district, with the selected-district card.
 * Scores come from placeholders (zero) until soil-health scoring exists; counts are real.
 */
const DistrictImpactPanel = ({
  scopeName, isStateScope, activeStates, districts, selectedDistrict,
  onSelectState, onSelectDistrict, onClearDistrict, bars, onBarsChange, score,
}) => {
  const gain = score.after - score.before;
  const gainLabel = `${gain >= 0 ? '+' : ''}${gain} pts`;
  const showBefore = bars !== 'after';
  const showAfter = bars !== 'before';

  const rows = isStateScope
    ? districts.map((d) => ({
      id: d.district,
      name: d.district,
      sub: `${fmt(d.soil_tests)} soil tests`,
      muted: d.is_unspecified,
      pressed: selectedDistrict === d.district,
      onClick: () => onSelectDistrict(selectedDistrict === d.district ? null : d.district),
    }))
    : activeStates.map((s) => ({
      id: s.key,
      name: s.state,
      sub: `${s.districts_covered} ${s.districts_covered === 1 ? 'district' : 'districts'}`,
      muted: false,
      pressed: false,
      onClick: () => onSelectState(s.key),
    }));

  const dd = isStateScope && selectedDistrict ? districts.find((d) => d.district === selectedDistrict) : null;

  return (
    <section aria-labelledby="csr-dl-h" className="csr-card csr-panel">
      <div className="d-flex flex-column" style={{ gap: 2 }}>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          <h2 id="csr-dl-h">{isStateScope ? `Soil health score · ${scopeName} districts` : 'Soil health score by state'}</h2>
          <span className="csr-chip csr-chip--amber">Pending data</span>
        </div>
        <p className="csr-panel__hint">
          {isStateScope
            ? 'Average score out of 100 in each district. Select a district to see its details.'
            : 'Average score out of 100, before and after intervention. Select a state to see its districts.'}
        </p>
      </div>

      <div role="group" aria-label="Intervention period" className="csr-seg">
        {BAR_MODES.map(([key, label]) => (
          <button key={key} type="button" aria-pressed={bars === key} onClick={() => onBarsChange(key)}>{label}</button>
        ))}
      </div>
      <div className="csr-panel__legend">
        <span><span className="csr-panel__key" style={{ background: '#8B5E3B' }} />Before intervention</span>
        <span><span className="csr-panel__key" style={{ background: '#145A32' }} />After intervention</span>
      </div>

      {dd ? (
        <div className="csr-district" aria-live="polite">
          <div className="csr-district__head">
            <div className="d-flex flex-column">
              <span className="csr-kpi__label">Selected district</span>
              <h3>{`${dd.district}, ${scopeName}`}</h3>
            </div>
            <button type="button" className="csr-district__close" onClick={onClearDistrict} aria-label="Clear selected district">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="csr-district__grid">
            <div className="csr-district__cell"><span>Soil tests · farmers</span><strong>{`${fmt(dd.soil_tests)} · ${fmt(dd.farmers)}`}</strong></div>
            <div className="csr-district__cell"><span>Acres covered</span><strong>{fmt(dd.acres)}</strong></div>
            <div className="csr-district__cell"><span>Soil health score</span><strong>{`${score.before} → ${score.after}`}</strong></div>
            <div className="csr-district__cell"><span>Score gain</span><strong>{`${gain >= 0 ? '+' : ''}${gain} points`}</strong></div>
          </div>
        </div>
      ) : null}

      <div className="csr-rows">
        {rows.length === 0 ? (
          <p className="csr-empty-rows">{isStateScope ? 'No districts recorded in this state yet.' : 'No active states yet.'}</p>
        ) : rows.map((r) => (
          <button key={r.id} type="button" className={`csr-row ${r.muted ? 'is-muted' : ''}`} aria-pressed={r.pressed} onClick={r.onClick}>
            <span className="csr-row__top">
              <span><strong>{r.name}</strong> <span className="csr-row__sub">{r.sub}</span></span>
              <strong>{gainLabel}</strong>
            </span>
            {showBefore ? <ScoreBar value={score.before} color="#8B5E3B" /> : null}
            {showAfter ? <ScoreBar value={score.after} color="#145A32" /> : null}
          </button>
        ))}
      </div>
    </section>
  );
};

export default DistrictImpactPanel;